import {defer, map, Observable} from 'rxjs';

import {computed, signal, Signal, WritableSignal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {uuidv4} from '@shared-types';

import {FileUploadTypeIcon} from './file-type-icon.const';
import {IFileModel} from './file.structure';
import {toFileUploadType} from './file.type';
import {StoredFile} from './stored-file';

export class FileModel {
	public uid: string = uuidv4();
	lastModified: Date;
	startPage?: number;

	constructor(public file: File) {
		this._name.set(file.name);
		this._size.set(file.size);
		this._type.set(file.type);
		this.lastModified = new Date(file.lastModified);
		this.arrayBuffer = () => defer(() => file.arrayBuffer());
	}

	async toJSON(): Promise<IFileModel> {
		const fileJSON = await this.base64().toPromise();
		const base64 = fileJSON.data;
		return {
			uid: this.uid,
			fileData: {
				text: base64,
				type: this.file.type,
				size: this.file.size,
				name: this.file.name,
				lastModified: this.file.lastModified,
				startPage: this.startPage,
			},
		};
	}

	public static fromJSON(fileJSON: IFileModel): FileModel {
		const base64 = fileJSON.fileData.text;
		const type = fileJSON.fileData.type;
		const name = fileJSON.fileData.name;
		const lastModified = fileJSON.fileData.lastModified;
		const byteCharacters = atob(base64);
		const byteArrays = [];
		for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
			const slice = byteCharacters.slice(offset, offset + 1024);
			const byteNumbers = new Array(slice.length);
			for (let i = 0; i < slice.length; i++) {
				byteNumbers[i] = slice.charCodeAt(i);
			}
			const byteArray = new Uint8Array(byteNumbers);
			byteArrays.push(byteArray);
		}
		const blob = new Blob(byteArrays, {type});
		const file = new File([blob], name, {lastModified});
		return new FileModel(file);
	}

	/* -------------------------------------------------- NAME -------------------------------------------------- */
	private _name: WritableSignal<string> = signal('');
	public readonly fullName: Signal<string> = this._name.asReadonly();
	public readonly name: Signal<string> = computed(() => {
		const name = this._name();
		return name.split('.').slice(0, -1).join('.');
	});

	/* -------------------------------------------------- TYPE -------------------------------------------------- */
	private _type: WritableSignal<string> = signal('');
	public readonly type: Signal<string> = this._type.asReadonly();
	public readonly typeIcon: Signal<string> = computed(() => {
		if (!this._type()) return '';
		const type = toFileUploadType(this._type());
		return `fa-${FileUploadTypeIcon[type]}`;
	});

	/* -------------------------------------------------- SIZE -------------------------------------------------- */
	private _size: WritableSignal<number> = signal(0);
	public readonly size: Signal<number> = this._size.asReadonly();

	public readonly normalizedSize: Signal<string> = computed(() => {
		const size = this._size();
		if (size < 1024) return `${size} B`;
		if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
		if (size < 1024 * 1024 * 1024) return `${(size / 1024 / 1024).toFixed(2)} MB`;
		if (size < 1024 * 1024 * 1024 * 1024) return `${(size / 1024 / 1024 / 1024).toFixed(2)} GB`;
		return `${(size / 1024 / 1024 / 1024 / 1024).toFixed(2)} TB`;
	});

	public readonly sizeInKb: Signal<string> = computed(() => `${(this._size() / 1024).toFixed(2)} KB`);
	public readonly sizeInMb: Signal<string> = computed(() => `${(this._size() / 1024 / 1024).toFixed(2)} MB`);
	public readonly sizeInGb: Signal<string> = computed(() => `${(this._size() / 1024 / 1024 / 1024).toFixed(2)} GB`);
	public readonly sizeInTb: Signal<string> = computed(() => `${(this._size() / 1024 / 1024 / 1024 / 1024).toFixed(2)} TB`);

	/* ------------------------------------------------ CONVERTS ------------------------------------------------ */
	public arrayBuffer: () => Observable<ArrayBuffer>;
	public base64(): Observable<StoredFile> {
		return this.arrayBuffer().pipe(
			map((arrayBuffer: ArrayBuffer) => {
				const base64: string = this.arrayBufferToBase64(arrayBuffer);
				return new StoredFile(base64, this.type(), this.name());
			}),
		);
	}

	/* -------------------------------------------------------------------------------------------------------------- */
	private _base64Signal: Signal<StoredFile>;
	public base64Signal(): Signal<StoredFile> {
		if (!this._base64Signal) {
			this._base64Signal = toSignal(this.base64());
		}
		return this._base64Signal;
	}

	private arrayBufferToBase64(buffer: ArrayBuffer): string {
		let binary = '';
		const bytes = new Uint8Array(buffer);
		const len = bytes.byteLength;

		for (let i = 0; i < len; i++) {
			binary += String.fromCharCode(bytes[i]);
		}

		return window.btoa(binary);
	}
}
